import React, { useState } from 'react';
import {
  Paper,
  Title,
  Stack,
  Group,
  Button,
  Select,
  Table,
  Badge,
  ScrollArea,
  Text,
  Tabs,
  Code,
  Alert
} from '@mantine/core';
import {
  IconDownload,
  IconFileSpreadsheet,
  IconFileText,
  IconFileCode,
  IconCheck,
  IconX,
  IconRefresh,
  IconArrowRight
} from '@tabler/icons-react';

import { IMPORT_ACTION } from '../import/importConstants';

/**
 * ImportLogViewer - Display and download import logs
 */
const ImportLogViewer = ({ importLog, importResults }) => {
  const [downloadFormat, setDownloadFormat] = useState('json');

  if (!importLog) {
    return null;
  }

  const summary = importLog.getSummary();
  const logs = importLog.getLogs();

  /**
   * Handle download button click
   */
  const handleDownload = () => {
    try {
      importLog.downloadLog(downloadFormat, 'aws-connect-import-log');
    } catch (error) {
      console.error('Error downloading log:', error);
      alert(`Failed to download log: ${error.message}`);
    }
  };

  /**
   * Get action icon
   */
  const getActionIcon = (action) => {
    switch (action) {
      case IMPORT_ACTION.CREATE:
        return <IconCheck size={14} />;
      case IMPORT_ACTION.UPDATE:
        return <IconRefresh size={14} />;
      case IMPORT_ACTION.SKIP:
        return <IconArrowRight size={14} />;
      case IMPORT_ACTION.ERROR:
        return <IconX size={14} />;
      default:
        return null;
    }
  };

  /**
   * Get action badge
   */
  const getActionBadge = (action) => {
    const colors = {
      [IMPORT_ACTION.CREATE]: 'green',
      [IMPORT_ACTION.UPDATE]: 'blue',
      [IMPORT_ACTION.SKIP]: 'gray',
      [IMPORT_ACTION.ERROR]: 'red'
    };

    return (
      <Badge color={colors[action] || 'gray'} leftSection={getActionIcon(action)}>
        {action.toUpperCase()}
      </Badge>
    );
  };

  /**
   * Format duration
   */
  const formatDuration = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    const seconds = Math.round(milliseconds / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Paper shadow="sm" p="md" withBorder>
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={3}>Import Log</Title>

          <Group gap="xs">
            <Select
              value={downloadFormat}
              onChange={setDownloadFormat}
              data={[
                { value: 'json', label: 'JSON' },
                { value: 'text', label: 'Text' },
                { value: 'csv', label: 'CSV' }
              ]}
              leftSection={
                downloadFormat === 'json' ? <IconFileCode size={16} /> :
                downloadFormat === 'csv' ? <IconFileSpreadsheet size={16} /> :
                <IconFileText size={16} />
              }
              w={120}
            />
            <Button
              leftSection={<IconDownload size={16} />}
              onClick={handleDownload}
              variant="light"
              color="aws"
            >
              Download Log
            </Button>
          </Group>
        </Group>

        {/* Summary */}
        <Alert icon={<IconCheck size={16} />} color="blue" title="Import Summary">
          <Group gap="xl">
            <Text size="sm">
              <strong>Duration:</strong> {formatDuration(summary.duration)}
            </Text>
            <Text size="sm" c="green">
              <strong>Created:</strong> {summary.created}
            </Text>
            <Text size="sm" c="blue">
              <strong>Updated:</strong> {summary.updated}
            </Text>
            <Text size="sm" c="gray">
              <strong>Skipped:</strong> {summary.skipped}
            </Text>
            <Text size="sm" c="red">
              <strong>Failed:</strong> {summary.failed}
            </Text>
          </Group>
        </Alert>

        {/* Log View Tabs */}
        <Tabs defaultValue="table">
          <Tabs.List>
            <Tabs.Tab value="table">Table View</Tabs.Tab>
            <Tabs.Tab value="text">Text View</Tabs.Tab>
            <Tabs.Tab value="json">JSON View</Tabs.Tab>
          </Tabs.List>

          {/* Table View */}
          <Tabs.Panel value="table" pt="md">
            <ScrollArea h={400}>
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Time</Table.Th>
                    <Table.Th>Action</Table.Th>
                    <Table.Th>Resource Type</Table.Th>
                    <Table.Th>Resource Name</Table.Th>
                    <Table.Th>Details</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {logs.map((log, index) => {
                    const time = new Date(log.timestamp).toLocaleTimeString();

                    return (
                      <Table.Tr key={index}>
                        <Table.Td>
                          <Text size="xs" c="dimmed">{time}</Text>
                        </Table.Td>
                        <Table.Td>{getActionBadge(log.action)}</Table.Td>
                        <Table.Td>
                          <Text size="sm">{log.resourceType}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" fw={500}>{log.resourceName}</Text>
                        </Table.Td>
                        <Table.Td>
                          {log.error ? (
                            <Text size="xs" c="red">{log.error}</Text>
                          ) : log.reason ? (
                            <Text size="xs" c="dimmed">{log.reason}</Text>
                          ) : log.id ? (
                            <Text size="xs" c="dimmed">ID: {log.id.substring(0, 20)}...</Text>
                          ) : null}
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Tabs.Panel>

          {/* Text View */}
          <Tabs.Panel value="text" pt="md">
            <ScrollArea h={400}>
              <Code block>
                {importLog.exportAsText()}
              </Code>
            </ScrollArea>
          </Tabs.Panel>

          {/* JSON View */}
          <Tabs.Panel value="json" pt="md">
            <ScrollArea h={400}>
              <Code block>
                {importLog.exportAsJson()}
              </Code>
            </ScrollArea>
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Paper>
  );
};

export default ImportLogViewer;
